import * as Schema from "effect/Schema";
import { ContentHash, Dimensions, HexColor, Identifier, IntegerTimeMs, NonEmptyText, NormalizedPoint, NormalizedRect, Positive, Rect, Revision, SafeInteger, TimeMs, freezeMetadata, hasUniqueIds, rectFits, } from "@linempo/kernel";
export const PROJECT_SCHEMA_VERSION = 3;
export const SceneMode = Schema.Literals(["clips", "continuous"]);
export const AssetDescriptor = Schema.Struct({
    id: Identifier,
    hash: ContentHash,
    name: NonEmptyText,
    mimeType: Schema.Literals(["image/png", "image/jpeg", "image/webp"]),
    dimensions: Dimensions,
    byteLength: SafeInteger.check(Schema.isGreaterThan(0)),
    /** Present only when import normalized the source. Canonical fields above describe stored bytes. */
    importSource: Schema.optionalKey(Schema.Struct({
        name: NonEmptyText,
        mimeType: Schema.Literals(["image/png", "image/jpeg", "image/webp"]),
        dimensions: Schema.Struct({
            width: SafeInteger.check(Schema.isGreaterThan(0), Schema.isLessThanOrEqualTo(32_768)),
            height: SafeInteger.check(Schema.isGreaterThan(0), Schema.isLessThanOrEqualTo(32_768)),
        }).check(Schema.makeFilter((size) => size.width * size.height <= 67_108_864 || "import source exceeds 64 megapixels")),
        byteLength: SafeInteger.check(Schema.isGreaterThan(0)),
    })),
});
/** Original cue order, absolute times, overlap, gaps and multiline text are retained. */
export const SubtitleCue = Schema.Struct({
    id: Identifier,
    sourceIndex: SafeInteger,
    startMs: IntegerTimeMs,
    endMs: IntegerTimeMs,
    text: NonEmptyText,
}).check(Schema.makeFilter((cue) => cue.endMs > cue.startMs || "subtitle endMs must exceed startMs"));
export const ProtectionRegion = Schema.Struct({ id: Identifier, rect: NormalizedRect });
export const ManualStroke = Schema.Struct({
    id: Identifier,
    points: Schema.Array(NormalizedPoint).check(Schema.isMinLength(2)),
    startMs: TimeMs,
    endMs: TimeMs,
    /** Source-image pixels, before placement scaling. */
    width: Positive,
    color: HexColor,
    /** Explicit release: all other scene protection rectangles remain excluded. */
    allowedProtectionIds: Schema.Array(Identifier),
}).check(Schema.makeFilter((stroke) => stroke.endMs > stroke.startMs || "stroke endMs must exceed startMs"));
export const FillRegion = Schema.Struct({
    id: Identifier,
    rect: NormalizedRect,
    color: HexColor,
    startMs: TimeMs,
    endMs: TimeMs,
    allowedProtectionIds: Schema.Array(Identifier),
}).check(Schema.makeFilter((fill) => fill.endMs > fill.startMs || "fill endMs must exceed startMs"));
export const ErasePolicy = Schema.Union([
    Schema.TaggedStruct("none", {}),
    Schema.TaggedStruct("step", { atMs: TimeMs }),
]);
export const AnnotationScene = Schema.Struct({
    id: Identifier,
    assetId: Identifier,
    animationId: Schema.optionalKey(Identifier),
    drawingDurationMs: Schema.optionalKey(TimeMs),
    automaticDrawDurationMs: Schema.optionalKey(TimeMs),
    cueIds: Schema.Array(Identifier),
    startMs: TimeMs,
    endMs: TimeMs,
    placement: Rect,
    drawMode: Schema.Literals(["automatic", "manual"]),
    manualStrokes: Schema.Array(ManualStroke),
    fillRegions: Schema.Array(FillRegion),
    protectionRegions: Schema.Array(ProtectionRegion),
    erase: ErasePolicy,
}).check(Schema.makeFilter((scene) => {
    if (scene.endMs <= scene.startMs)
        return "scene endMs must exceed startMs";
    if (scene.erase._tag === "step" && scene.erase.atMs < scene.endMs)
        return "step erase must not precede scene end";
    if (!hasUniqueIds([...scene.manualStrokes, ...scene.fillRegions, ...scene.protectionRegions]))
        return "annotation IDs must be unique within a scene";
    if (scene.drawMode === "manual" && scene.manualStrokes.length === 0)
        return "manual mode requires a stroke";
    const protectedIds = new Set(scene.protectionRegions.map((region) => region.id));
    for (const operation of [...scene.manualStrokes, ...scene.fillRegions]) {
        if (operation.startMs < scene.startMs || operation.endMs > scene.endMs)
            return "annotation time must lie inside scene time";
        if (operation.allowedProtectionIds.some((id) => !protectedIds.has(id)))
            return "unknown released protection region";
    }
    return undefined;
}));
const subtitleTrackFields = {
    id: Identifier,
    cues: Schema.Array(SubtitleCue).check(Schema.makeFilter((cues) => hasUniqueIds(cues) || "duplicate cue ID")),
};
export const SubtitleTrack = Schema.TaggedStruct("subtitles", {
    ...subtitleTrackFields,
    /** Import provenance is optional so existing projects retain their exact cue metadata. */
    metadata: Schema.optionalKey(Schema.Struct({ sourceHash: ContentHash, sourceName: Schema.optionalKey(NonEmptyText) })),
});
const LegacySceneTrack = Schema.TaggedStruct("scenes", {
    id: Identifier,
    scenes: Schema.Array(AnnotationScene).check(Schema.makeFilter((scenes) => hasUniqueIds(scenes) || "duplicate scene ID")),
});
const LegacyTimelineTrack = Schema.Union([SubtitleTrack, LegacySceneTrack]);
const projectFields = {
    id: Identifier,
    name: NonEmptyText,
    revision: Revision,
    canvas: Dimensions,
    durationMs: TimeMs,
    assets: Schema.Array(AssetDescriptor),
};
function projectInvariant(project) {
    if (!hasUniqueIds(project.assets) || !hasUniqueIds(project.tracks))
        return "duplicate asset or track ID";
    const cues = project.tracks.flatMap((track) => track._tag === "subtitles" ? [...track.cues] : []);
    const scenes = project.tracks.flatMap((track) => track._tag === "scenes" ? [...track.scenes] : []);
    if (!hasUniqueIds(cues) || !hasUniqueIds(scenes))
        return "cue and scene IDs must be globally unique within their kind";
    const cueIds = new Set(cues.map((cue) => cue.id));
    const assetIds = new Set(project.assets.map((asset) => asset.id));
    if (cues.some((cue) => cue.endMs > project.durationMs))
        return "subtitle exceeds project duration";
    for (const scene of scenes) {
        if (!assetIds.has(scene.assetId) || scene.cueIds.some((id) => !cueIds.has(id)))
            return "scene refers to an unknown asset or cue";
        if (!rectFits(scene.placement, project.canvas))
            return "scene placement exceeds canvas bounds";
        const endMs = project.sceneMode !== "clips" && scene.erase._tag === "step" ? scene.erase.atMs : scene.endMs;
        if (endMs > project.durationMs)
            return "scene or erase exceeds project duration";
    }
    if (project.sceneMode === "clips") {
        const ordered = [...scenes].sort((a, b) => a.startMs - b.startMs);
        for (let index = 1; index < ordered.length; index++) {
            if (ordered[index].startMs < ordered[index - 1].endMs)
                return "clips scenes must not overlap across tracks";
        }
    }
    return undefined;
}
/** Strict legacy schema: validation happens before migration, with no inferred or repaired fields. */
export const AnnotationProjectV1 = Schema.Struct({
    ...projectFields,
    schemaVersion: Schema.Literal(1),
    tracks: Schema.Array(Schema.Union([Schema.TaggedStruct("subtitles", subtitleTrackFields), LegacySceneTrack])),
}).check(Schema.makeFilter(projectInvariant));
export const AnnotationProjectV2 = Schema.Struct({
    ...projectFields,
    schemaVersion: Schema.Literal(2),
    sceneMode: SceneMode,
    tracks: Schema.Array(LegacyTimelineTrack),
}).check(Schema.makeFilter(projectInvariant));
export const DrawingDuration = Schema.Union([
    Schema.TaggedStruct("fit", {}),
    Schema.TaggedStruct("custom", { durationMs: TimeMs.check(Schema.isGreaterThan(0)) }),
]);
export const HandStyle = Schema.Literals(["pen", "hidden"]);
export const AnimationDefinition = Schema.Struct({
    id: Identifier,
    assetId: Identifier,
    drawMode: Schema.Literals(["automatic", "manual"]),
    manualStrokes: Schema.Array(ManualStroke),
    fillRegions: Schema.Array(FillRegion),
    protectionRegions: Schema.Array(ProtectionRegion),
    /** Local annotation time basis; geometry remains in normalized source coordinates. */
    localDurationMs: TimeMs.check(Schema.isGreaterThan(0)),
    drawingDuration: DrawingDuration,
    /** Absent on early v3 snapshots: preserve their original fit timing. */
    fitTiming: Schema.optionalKey(Schema.Literals(["legacy", "completed-frame"])),
    lastSceneDurationMs: TimeMs.check(Schema.isGreaterThan(0)),
}).check(Schema.makeFilter((animation) => {
    if (!hasUniqueIds([...animation.manualStrokes, ...animation.fillRegions, ...animation.protectionRegions]))
        return "duplicate annotation ID";
    if (animation.drawMode === "manual" && !animation.manualStrokes.length)
        return "manual mode requires a stroke";
    const ids = new Set(animation.protectionRegions.map((region) => region.id));
    for (const operation of [...animation.manualStrokes, ...animation.fillRegions]) {
        if (operation.endMs > animation.localDurationMs)
            return "annotation exceeds local animation time";
        if (operation.allowedProtectionIds.some((id) => !ids.has(id)))
            return "unknown released protection region";
    }
    return undefined;
}));
export const SceneReference = Schema.Struct({
    id: Identifier, animationId: Identifier, cueIds: Schema.Array(Identifier),
    startMs: TimeMs, endMs: TimeMs, placement: Rect, erase: ErasePolicy,
}).check(Schema.makeFilter((scene) => scene.endMs <= scene.startMs ? "scene endMs must exceed startMs"
    : scene.erase._tag === "step" && scene.erase.atMs < scene.endMs ? "step erase must not precede scene end" : undefined));
export const SceneTrack = Schema.TaggedStruct("scenes", {
    id: Identifier, scenes: Schema.Array(SceneReference).check(Schema.makeFilter((scenes) => hasUniqueIds(scenes) || "duplicate scene ID")),
});
export const TimelineTrack = Schema.Union([SubtitleTrack, SceneTrack]);
export const COMPLETED_HOLD_MS = 1000 / 30;
export const MAX_PROJECT_DURATION_MS = 600_000;
/** The historical automatic drawing endpoint; fit mode keeps this timing unchanged. */
export const automaticLocalEnd = (animation) => animation.drawMode === "automatic" ? animation.localDurationMs - Math.min(500, animation.localDurationMs * 0.15) : 0;
export const intrinsicDrawingEnd = (animation) => Math.max(automaticLocalEnd(animation), ...animation.manualStrokes.map((stroke) => stroke.endMs), ...animation.fillRegions.map((fill) => fill.endMs));
export const animationTimeScale = (animation, sceneDurationMs) => animation.drawingDuration._tag === "fit" ? (sceneDurationMs - (animation.fitTiming === "completed-frame" ? COMPLETED_HOLD_MS : 0)) / animation.localDurationMs
    : animation.drawingDuration.durationMs / intrinsicDrawingEnd(animation);
export const automaticDrawDuration = (animation, sceneDurationMs) => animation.drawMode !== "automatic" ? 0 : animation.drawingDuration._tag === "fit"
    ? Math.min(sceneDurationMs - Math.min(500, sceneDurationMs * 0.15), sceneDurationMs - (animation.fitTiming === "completed-frame" ? COMPLETED_HOLD_MS : 0))
    : automaticLocalEnd(animation) * animationTimeScale(animation, sceneDurationMs);
export const effectiveDrawingDuration = (animation, sceneDurationMs) => Math.max(automaticDrawDuration(animation, sceneDurationMs), ...[...animation.manualStrokes, ...animation.fillRegions]
    .map((operation) => operation.endMs * animationTimeScale(animation, sceneDurationMs)));
export const AnnotationProject = Schema.Struct({
    ...projectFields, schemaVersion: Schema.Literal(PROJECT_SCHEMA_VERSION), sceneMode: SceneMode,
    animations: Schema.Array(AnimationDefinition), settings: Schema.Struct({ handStyle: HandStyle }),
    tracks: Schema.Array(TimelineTrack),
}).check(Schema.makeFilter((project) => {
    if (!hasUniqueIds(project.animations))
        return "duplicate animation ID";
    const assets = new Set(project.assets.map((asset) => asset.id));
    if (project.animations.some((animation) => !assets.has(animation.assetId)))
        return "animation refers to unknown asset";
    if (project.assets.some((asset) => !project.animations.some((animation) => animation.assetId === asset.id)))
        return "asset has no animation definition";
    const animations = new Map(project.animations.map((animation) => [animation.id, animation]));
    for (const scene of sceneReferences(project)) {
        const animation = animations.get(scene.animationId);
        if (!animation)
            return "scene refers to unknown animation";
        if (animation.fitTiming === "completed-frame" && scene.endMs - scene.startMs <= COMPLETED_HOLD_MS)
            return "scene requires time to draw and a completed hold frame";
        if (animation.drawingDuration._tag === "custom" && animation.drawingDuration.durationMs + COMPLETED_HOLD_MS > scene.endMs - scene.startMs + 1e-7)
            return "custom drawing requires a completed hold frame";
    }
    return projectInvariant({ ...project, tracks: project.tracks.map((track) => track._tag !== "scenes" ? track
            : { ...track, scenes: track.scenes.map((scene) => resolveAnimationScene(animations.get(scene.animationId), scene)) }) });
}));
/** Deterministic bounded IDs preserve common names; collisions get a suffix within the same limit. */
export function allocateAnimationId(preferred, existingIds = []) {
    const occupied = new Set(existingIds);
    const base = preferred.slice(0, 128);
    if (!occupied.has(base))
        return base;
    for (let index = 1;; index++) {
        const suffix = `-${index}`;
        const candidate = `${preferred.slice(0, 128 - suffix.length)}${suffix}`;
        if (!occupied.has(candidate))
            return candidate;
    }
}
export const defaultAnimation = (assetId, id = allocateAnimationId(`animation-${assetId}`)) => ({
    id, assetId, drawMode: "automatic", manualStrokes: [], fillRegions: [], protectionRegions: [],
    localDurationMs: 3000, drawingDuration: { _tag: "fit" }, fitTiming: "completed-frame", lastSceneDurationMs: 3000,
});
export function definitionFromScene(scene, id, fitTiming = "completed-frame") {
    const local = (item) => ({ ...item,
        startMs: item.startMs - scene.startMs, endMs: item.endMs - scene.startMs });
    return { id, assetId: scene.assetId, drawMode: scene.drawMode, manualStrokes: scene.manualStrokes.map(local),
        fillRegions: scene.fillRegions.map(local), protectionRegions: scene.protectionRegions,
        fitTiming, localDurationMs: scene.endMs - scene.startMs, lastSceneDurationMs: scene.endMs - scene.startMs, drawingDuration: { _tag: "fit" } };
}
export function referenceFromScene(scene, animationId) {
    return { id: scene.id, animationId, cueIds: scene.cueIds, startMs: scene.startMs, endMs: scene.endMs, placement: scene.placement, erase: scene.erase };
}
export function resolveAnimationScene(animation, scene) {
    const duration = scene.endMs - scene.startMs;
    const scale = animationTimeScale(animation, duration);
    const absolute = (item) => ({ ...item,
        startMs: scene.startMs + item.startMs * scale, endMs: scene.startMs + item.endMs * scale });
    return { ...scene, assetId: animation.assetId, drawMode: animation.drawMode,
        manualStrokes: animation.manualStrokes.map(absolute), fillRegions: animation.fillRegions.map(absolute),
        protectionRegions: animation.protectionRegions, drawingDurationMs: effectiveDrawingDuration(animation, duration),
        automaticDrawDurationMs: automaticDrawDuration(animation, duration) };
}
/** Pure read migration; independent definitions preserve every legacy duplicate's annotations. */
export const decodeProject = (input) => {
    if (typeof input === "object" && input !== null && "schemaVersion" in input && (input.schemaVersion === 1 || input.schemaVersion === 2)) {
        const legacy = input.schemaVersion === 1 ? Schema.decodeUnknownSync(AnnotationProjectV1)(input) : Schema.decodeUnknownSync(AnnotationProjectV2)(input);
        const animations = [];
        const allocatedIds = new Set();
        const allocate = (preferred) => {
            const id = allocateAnimationId(preferred, allocatedIds);
            allocatedIds.add(id);
            return id;
        };
        const tracks = legacy.tracks.map((track) => track._tag !== "scenes" ? track : { ...track, scenes: track.scenes.map((scene) => {
                const animation = definitionFromScene(scene, allocate(`scene-animation-${scene.id}`), "legacy");
                animations.push(animation);
                return referenceFromScene(scene, animation.id);
            }) });
        for (const asset of legacy.assets)
            if (!animations.some((animation) => animation.assetId === asset.id))
                animations.push(defaultAnimation(asset.id, allocate(`animation-${asset.id}`)));
        return freezeMetadata(Schema.decodeUnknownSync(AnnotationProject)({ ...legacy, schemaVersion: PROJECT_SCHEMA_VERSION,
            sceneMode: "sceneMode" in legacy ? legacy.sceneMode : "continuous", settings: { handStyle: "pen" }, animations, tracks }));
    }
    return freezeMetadata(Schema.decodeUnknownSync(AnnotationProject)(input));
};
export const subtitleCues = (project) => project.tracks.flatMap((track) => track._tag === "subtitles" ? [...track.cues] : []);
export const sceneReferences = (project) => project.tracks.flatMap((track) => track._tag === "scenes" ? [...track.scenes] : []);
export const animationForAsset = (project, assetId) => project.animations.find((animation) => animation.assetId === assetId);
export const animationForScene = (project, sceneId) => project.animations.find((animation) => animation.id === sceneReferences(project).find((scene) => scene.id === sceneId)?.animationId);
export const assetByHash = (project, hash) => project.assets.find((asset) => asset.hash === hash);
export const annotationScenes = (project) => {
    const animations = new Map(project.animations.map((animation) => [animation.id, animation]));
    return sceneReferences(project).map((scene) => resolveAnimationScene(animations.get(scene.animationId), scene));
};
/** Standalone preview uses source dimensions and the retained duration, including a custom hold frame. */
export function animationScene(project, animationId) {
    const animation = project.animations.find((item) => item.id === animationId);
    if (!animation)
        throw new Error("Unknown animation");
    const asset = project.assets.find((item) => item.id === animation.assetId);
    const endMs = Math.max(animation.lastSceneDurationMs, animation.fitTiming === "completed-frame" ? 2 * COMPLETED_HOLD_MS : 0, animation.drawingDuration._tag === "custom" ? animation.drawingDuration.durationMs + COMPLETED_HOLD_MS : 0);
    return resolveAnimationScene(animation, { id: allocateAnimationId(`preview-${animation.id}`), animationId, cueIds: [], startMs: 0, endMs,
        placement: { x: 0, y: 0, ...asset.dimensions }, erase: { _tag: "none" } });
}
/** Deterministic source key, not a cryptographic digest. Timing, placement and speed are excluded. */
export function definitionGeometryKey(animation, assetHash) {
    const rectKey = (rect) => JSON.stringify([rect.x, rect.y, rect.width, rect.height]);
    // Region IDs are editable handles, never geometry identity. Duplicate rectangles have the same mask.
    const protectionRects = [...new Set(animation.protectionRegions.map((region) => rectKey(region.rect)))].sort();
    const protectionIndexes = new Map(protectionRects.map((rect, index) => [rect, index]));
    const exclusions = (allowedIds) => {
        const allowed = new Set(allowedIds);
        return [...new Set(animation.protectionRegions.filter((region) => !allowed.has(region.id))
                .map((region) => protectionIndexes.get(rectKey(region.rect))))].sort((a, b) => a - b);
    };
    return JSON.stringify({ version: 2, assetHash, drawMode: animation.drawMode, protectionRects,
        manualStrokes: animation.manualStrokes.map((stroke) => ({ points: stroke.points.map((point) => [point.x, point.y]),
            width: stroke.width, color: stroke.color, excludedProtectionIndexes: exclusions(stroke.allowedProtectionIds) })),
        fillRegions: animation.fillRegions.map((fill) => ({ rect: rectKey(fill.rect), color: fill.color,
            excludedProtectionIndexes: exclusions(fill.allowedProtectionIds) })) });
}
export function animationGeometryKey(project, animationId) {
    const animation = project.animations.find((item) => item.id === animationId);
    if (!animation)
        throw new Error("Unknown animation");
    return definitionGeometryKey(animation, project.assets.find((asset) => asset.id === animation.assetId).hash);
}
/** Stable source ordering is retained when simultaneous captions are active. */
export const activeSubtitleCues = (project, timeMs) => {
    Schema.decodeUnknownSync(TimeMs)(timeMs);
    return subtitleCues(project).filter((cue) => cue.startMs <= timeMs && timeMs < cue.endMs);
};
export function reviseProject(project, changes) {
    return decodeProject({ ...project, ...changes, revision: project.revision + 1 });
}
